import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  type Plugin,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { operationService } from '../services/operationService';
import { adminService } from '../services/adminService';
import { Operation, Team, AuditLog } from '../types';
import { parseApiDateTime } from '../utils/dateTime';

(jsPDF as any).autoTable = autoTable;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const ASM_RED = 'rgb(201, 31, 12)';
const ASM_RED_SOFT = 'rgba(201, 31, 12, 0.15)';
const CHART_ORANGE = 'rgb(230, 81, 0)';
const CHART_MAYDAY = 'rgb(136, 14, 79)';

const chartFont = {
  family: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

/** Werte direkt auf horizontalen Balken (lesbar ohne Tooltip). */
const horizontalBarInlineValuesPlugin = (format: 'count' | 'minutes'): Plugin<'bar'> => ({
  id: `asmHBarVals_${format}`,
  afterDatasetsDraw(chart) {
    if (chart.options.indexAxis !== 'y') return;
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;
    const dataset = chart.data.datasets[0];
    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.font = `600 11px ${chartFont.family}`;
    meta.data.forEach((element, index) => {
      const raw = dataset.data[index];
      const val = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isNaN(val) || val <= 0) return;
      const text = format === 'minutes' ? `${val.toFixed(1)} min` : String(Math.round(val));
      const { x, y, base } = (element as unknown as { getProps: (k: string[], z: boolean) => { x: number; y: number; base: number } }).getProps(
        ['x', 'y', 'base'],
        true
      );
      const barStart = Math.min(x, base);
      const barEnd = Math.max(x, base);
      const barW = barEnd - barStart;
      const tw = ctx.measureText(text).width;
      ctx.textAlign = 'left';
      if (barW > tw + 14) {
        ctx.fillStyle = 'rgba(255,255,255,0.96)';
        ctx.fillText(text, barStart + 6, y);
      } else {
        ctx.fillStyle = '#1a1a1a';
        ctx.fillText(text, Math.min(barEnd + 6, chartArea.right - tw - 2), y);
      }
    });
    ctx.restore();
  },
});

const kpiAccent = {
  ops: ASM_RED,
  team: CHART_ORANGE,
  mayday: CHART_MAYDAY,
  supervisors: 'rgb(84, 110, 122)',
  avgDur: 'rgb(183, 28, 28)',
  totalDur: 'rgb(191, 54, 12)',
  insight: 'rgb(55, 71, 79)',
  trend: 'rgb(25, 118, 210)',
} as const;

/** Einheitliche äußere Card für Statistik-Panels (Einordnung, Trend, …). */
const statsPanelShell: React.CSSProperties = {
  borderRadius: 10,
  padding: '16px 18px',
  background: 'linear-gradient(180deg, #fff 0%, #f6f8fc 100%)',
  border: '1px solid #dce3ee',
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  minWidth: 0,
};

const statsPanelTitle: React.CSSProperties = {
  margin: '0 0 4px 0',
  fontSize: 16,
  fontWeight: 700,
  color: '#1a1a1a',
};

const statsPanelPeriod: React.CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: 12,
  color: '#888',
  fontWeight: 600,
};

const statsPanelIntro: React.CSSProperties = {
  margin: '0 0 14px 0',
  fontSize: 12,
  color: '#666',
  lineHeight: 1.45,
};

const statsPanelFooter: React.CSSProperties = {
  marginTop: 10,
  paddingTop: 10,
  fontSize: 11,
  color: '#888',
  lineHeight: 1.4,
  borderTop: '1px solid #eceff4',
};

type OperationBundle = {
  operation: Operation;
  teams: Team[];
  logs: AuditLog[];
};

type ScopedStats = {
  operationCount: number;
  teamStartCount: number;
  maydayCount: number;
  uniqueSupervisors: number;
  avgTeamDurationMinutes: number;
  totalTeamDurationMinutes: number;
  /** Summe aller Trupp-Zeiten (Personen können mehrfach zählen). */
  totalMemberMinutesSum: number;
  /** Personen mit mindestens einer Atemschutz-Zeit > 0. */
  respondersWithTimeCount: number;
  topByEntries: Array<{ name: string; count: number }>;
  topByDuration: Array<{ name: string; minutes: number }>;
  byMonth: Array<{ month: string; operationCount: number; teamStartCount: number; maydayCount: number }>;
  /** Ø „Trupp gestartet“ je namentlich erfasstem Truppüberwacher (Proxy, Lastverteilung). */
  avgTeamsPerSupervisor: number;
  /** Mayday-Ereignisse je Einsatz. */
  maydayPerOperation: number;
  /** Ø unterschiedliche Truppüberwacher je Einsatz (aus Log performedBy). */
  avgSupervisorsPerOperation: number;
  /** Ø gestartete Trupps je Einsatz. */
  avgTeamsPerOperation: number;
  /** Ø Minuten im Gerät pro Person mit messbarer Einsatzzeit. */
  avgMinutesPerResponder: number;
};

const monthNames = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const parseMembers = (team: Team): string[] => {
  if (team.mitglieder && team.mitglieder.length > 0) {
    return team.mitglieder
      .map((m) => `${m.vorname || ''} ${m.nachname || ''}`.trim())
      .filter(Boolean);
  }
  if (team.membersString) {
    return team.membersString
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const getTeamDurationSeconds = (team: Team, logs: AuditLog[]): number => {
  const teamLogs = logs.filter((l) => l.teamID === team.id && l.created_at);
  if (teamLogs.length === 0) return 0;

  const startLog = teamLogs.find((l) => l.logType === 'Trupp gestartet');
  const start = startLog?.created_at ? parseApiDateTime(startLog.created_at)?.getTime() : null;
  const end = Math.max(
    ...teamLogs
      .map((l) => parseApiDateTime(l.created_at || '')?.getTime() || 0)
      .filter((v) => v > 0)
  );
  if (!start || !end || end <= start) return 0;
  return Math.floor((end - start) / 1000);
};

const formatMinutes = (minutes: number): string => `${minutes.toFixed(1)} min`;

const formatRatio = (n: number, digits = 2): string => (Number.isFinite(n) ? n.toFixed(digits) : '0');

/** Relativer Unterschied in %; null wenn nicht sinnvoll (Vergleichswert 0). */
function relChangePercent(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}

function computeScopedStatsFromSource(source: OperationBundle[]): ScopedStats {
  const byMonthBase = monthNames.map((month) => ({
    month,
    operationCount: 0,
    teamStartCount: 0,
    maydayCount: 0,
  }));

  const byEntries = new Map<string, number>();
  const byDuration = new Map<string, number>();
  const supervisors = new Set<string>();
  let teamDurationTotalSec = 0;
  let teamDurationCount = 0;
  let teamStartCount = 0;
  let maydayCount = 0;
  let supervisorsPerOpSum = 0;

  source.forEach((bundle) => {
    const opDate = parseApiDateTime(bundle.operation.created);
    const monthIdx = opDate ? opDate.getMonth() : 0;
    byMonthBase[monthIdx].operationCount += 1;

    const startLogs = bundle.logs.filter((l) => l.logType === 'Trupp gestartet');
    teamStartCount += startLogs.length;
    byMonthBase[monthIdx].teamStartCount += startLogs.length;

    const mayday = bundle.logs.filter((l) => (l.logType || '').toLowerCase().includes('mayday')).length;
    maydayCount += mayday;
    byMonthBase[monthIdx].maydayCount += mayday;

    const supervisorsThisOp = new Set<string>();
    bundle.logs.forEach((l) => {
      if (!l.logData) return;
      try {
        const parsed = typeof l.logData === 'string' ? JSON.parse(l.logData) : l.logData;
        if (typeof parsed?.performedBy === 'string' && parsed.performedBy.trim()) {
          const name = parsed.performedBy.trim();
          supervisors.add(name);
          supervisorsThisOp.add(name);
        }
      } catch {
        // ignore invalid logData
      }
    });
    supervisorsPerOpSum += supervisorsThisOp.size;

    bundle.teams.forEach((team) => {
      const members = parseMembers(team);
      const durationSec = getTeamDurationSeconds(team, bundle.logs);
      teamDurationTotalSec += durationSec;
      teamDurationCount += 1;
      members.forEach((m) => {
        byEntries.set(m, (byEntries.get(m) || 0) + 1);
        byDuration.set(m, (byDuration.get(m) || 0) + durationSec);
      });
    });
  });

  const topByEntries = Array.from(byEntries.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const topByDuration = Array.from(byDuration.entries())
    .map(([name, sec]) => ({ name, minutes: sec / 60 }))
    .sort((a, b) => b.minutes - a.minutes)
    .slice(0, 10);

  const opCount = source.length;
  const uniqSup = supervisors.size;
  let totalMemberSec = 0;
  let respondersWithTime = 0;
  byDuration.forEach((sec) => {
    totalMemberSec += sec;
    if (sec > 0) respondersWithTime += 1;
  });

  return {
    operationCount: opCount,
    teamStartCount,
    maydayCount,
    uniqueSupervisors: uniqSup,
    avgTeamDurationMinutes: teamDurationCount ? teamDurationTotalSec / 60 / teamDurationCount : 0,
    totalTeamDurationMinutes: teamDurationTotalSec / 60,
    totalMemberMinutesSum: totalMemberSec / 60,
    respondersWithTimeCount: respondersWithTime,
    topByEntries,
    topByDuration,
    byMonth: byMonthBase,
    avgTeamsPerSupervisor: uniqSup > 0 ? teamStartCount / uniqSup : 0,
    maydayPerOperation: opCount > 0 ? maydayCount / opCount : 0,
    avgSupervisorsPerOperation: opCount > 0 ? supervisorsPerOpSum / opCount : 0,
    avgTeamsPerOperation: opCount > 0 ? teamStartCount / opCount : 0,
    avgMinutesPerResponder: respondersWithTime > 0 ? totalMemberSec / 60 / respondersWithTime : 0,
  };
}

interface YearlyStatisticsProps {
  orgID: number;
}

const InsightMomRow: React.FC<{
  label: string;
  current: number;
  previous: number;
  format: (n: number) => string;
}> = ({ label, current, previous, format }) => {
  const pct = relChangePercent(current, previous);

  let color = '#5c6370';
  let rightText = '—';
  let direction: 'up' | 'down' | null = null;

  if (pct === null) {
    if (previous === 0 && current !== 0) {
      rightText = 'neu';
      direction = 'up';
      color = '#2e7d32';
    } else if (previous === 0 && current === 0) {
      rightText = '±0 %';
    }
  } else if (Math.abs(pct) < 0.05) {
    rightText = '±0 %';
  } else {
    direction = pct > 0 ? 'up' : 'down';
    color = pct > 0 ? '#2e7d32' : '#c62828';
    rightText = `${pct >= 0 ? '+' : ''}${pct.toFixed(1)} %`;
  }

  const TrendArrow: React.FC<{ direction: 'up' | 'down'; color: string }> = ({ direction, color }) => (
    <svg width="12" height="10" viewBox="0 0 12 10" style={{ display: 'block' }}>
      {direction === 'up' ? (
        <>
          <path d="M6 9 L6 2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
          <path d="M6 2 L3.4 4.4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
          <path d="M6 2 L8.6 4.4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        </>
      ) : (
        <>
          <path d="M6 1 L6 8" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
          <path d="M6 8 L3.4 5.6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
          <path d="M6 8 L8.6 5.6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        </>
      )}
    </svg>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 10,
        alignItems: 'start',
        padding: '10px 0',
        borderBottom: '1px solid #eceff4',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1a1a1a', fontSize: 13, lineHeight: 1.3 }}>{label}</div>
        <div style={{ fontSize: 11, color: '#666', marginTop: 3, lineHeight: 1.35 }}>
          <span style={{ color: '#333' }}>{format(current)}</span>
          <span style={{ color: '#999', margin: '0 4px' }}>·</span>
          <span>Vorm. {format(previous)}</span>
        </div>
      </div>
      <div style={{ color, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', paddingTop: 2, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        {direction ? <TrendArrow direction={direction} color={color} /> : null}
        <span>{rightText}</span>
      </div>
    </div>
  );
};

const YearlyStatistics: React.FC<YearlyStatisticsProps> = ({ orgID }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>(new Date().getMonth());
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState('');
  const [bundles, setBundles] = useState<OperationBundle[]>([]);
  const [prevYearDecemberBundles, setPrevYearDecemberBundles] = useState<OperationBundle[] | null>(null);

  useEffect(() => {
    let active = true;

    const loadBundlesForOps = async (ops: Operation[]): Promise<OperationBundle[]> => {
      return Promise.all(
        ops.map(async (operation) => {
          const [teams, logs] = await Promise.all([
            operationService.getTeamsByOperation(orgID, operation.id),
            operationService.getAuditLogsByOperation(orgID, operation.id),
          ]);
          return { operation, teams, logs };
        })
      );
    };

    const load = async () => {
      setLoading(true);
      setBackgroundLoading(false);
      setError('');
      setBundles([]);
      try {
        const ops = await operationService.getOperationsByYear([orgID], selectedYear);
        const asmOps = ops.filter((o) => o.asm_teams === true);

        const preferredMonth = selectedMonth === 'all' ? new Date().getMonth() : selectedMonth;
        const monthOps = asmOps.filter((o) => {
          const dt = parseApiDateTime(o.created);
          return dt ? dt.getMonth() === preferredMonth : false;
        });
        const restOps = asmOps.filter((o) => !monthOps.some((m) => m.id === o.id));

        // Phase 1: selected/current month first for fast first paint
        const firstBundles = await loadBundlesForOps(monthOps);
        if (!active) return;
        setBundles(firstBundles);
        setLoading(false);

        // Phase 2: rest of selected year in background
        if (restOps.length > 0) {
          setBackgroundLoading(true);
          const restBundles = await loadBundlesForOps(restOps);
          if (!active) return;
          setBundles((prev) => [...prev, ...restBundles]);
          setBackgroundLoading(false);
        }
      } catch (e: any) {
        if (active) setError(e?.message || 'Fehler beim Laden der Jahresstatistik');
        if (active) {
          setLoading(false);
          setBackgroundLoading(false);
        }
      }
    };
    load();
    return () => {
      active = false;
    };
    // Monatsfilter nur clientseitig auf bundles; kein Refetch bei Monatswechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedMonth bewusst ausgelassen
  }, [orgID, selectedYear]);

  // Für Januar: Vergleich mit Dezember des Vorjahres (ohne komplettes Refetch der Seite)
  useEffect(() => {
    let active = true;

    const loadPrevDecember = async () => {
      if (selectedMonth !== 0) {
        setPrevYearDecemberBundles(null);
        return;
      }
      setPrevYearDecemberBundles(null);
      try {
        const prevYear = selectedYear - 1;
        const opsPrevYear = await operationService.getOperationsByYear([orgID], prevYear);
        const asmPrevYear = opsPrevYear.filter((o) => o.asm_teams === true);
        const decemberOps = asmPrevYear.filter((o) => {
          const dt = parseApiDateTime(o.created);
          return dt ? dt.getMonth() === 11 : false;
        });

        const bundlesPrevDec = await Promise.all(
          decemberOps.map(async (operation) => {
            const [teams, logs] = await Promise.all([
              operationService.getTeamsByOperation(orgID, operation.id),
              operationService.getAuditLogsByOperation(orgID, operation.id),
            ]);
            return { operation, teams, logs };
          })
        );

        if (!active) return;
        setPrevYearDecemberBundles(bundlesPrevDec);
      } catch {
        if (!active) return;
        // Falls Vorjahr nicht verfügbar ist, einfach keinen Vergleich anzeigen.
        setPrevYearDecemberBundles([]);
      }
    };

    void loadPrevDecember();
    return () => {
      active = false;
    };
  }, [orgID, selectedYear, selectedMonth]);

  const scopedStats = useMemo<ScopedStats>(() => {
    const source = bundles.filter((b) => {
      if (selectedMonth === 'all') return true;
      const dt = parseApiDateTime(b.operation.created);
      return dt ? dt.getMonth() === selectedMonth : false;
    });
    return computeScopedStatsFromSource(source);
  }, [bundles, selectedMonth]);

  const prevMonthStats = useMemo<ScopedStats | null>(() => {
    if (selectedMonth === 'all') return null;
    if (selectedMonth === 0) {
      // Januar vergleicht mit Dezember des Vorjahres
      if (prevYearDecemberBundles === null) return null;
      return computeScopedStatsFromSource(prevYearDecemberBundles);
    }

    const prevMonthIndex = selectedMonth - 1;
    const prevSource = bundles.filter((b) => {
      const dt = parseApiDateTime(b.operation.created);
      return dt ? dt.getMonth() === prevMonthIndex : false;
    });
    return computeScopedStatsFromSource(prevSource);
  }, [bundles, prevYearDecemberBundles, selectedMonth]);

  const monthShortLabels = useMemo(() => monthNames.map((m) => m.slice(0, 3)), []);

  const yearTrendChartData = useMemo(
    () => ({
      labels: monthShortLabels,
      datasets: [
        {
          label: 'Atemschutzeinsätze',
          data: scopedStats.byMonth.map((m) => m.operationCount),
          borderColor: ASM_RED,
          backgroundColor: ASM_RED_SOFT,
          tension: 0.25,
          fill: true,
        },
        {
          label: 'Trupp gestartet',
          data: scopedStats.byMonth.map((m) => m.teamStartCount),
          borderColor: CHART_ORANGE,
          backgroundColor: 'rgba(230, 81, 0, 0.12)',
          tension: 0.25,
          fill: true,
        },
        {
          label: 'Mayday',
          data: scopedStats.byMonth.map((m) => m.maydayCount),
          borderColor: CHART_MAYDAY,
          backgroundColor: 'rgba(136, 14, 79, 0.12)',
          tension: 0.25,
          fill: true,
        },
      ],
    }),
    [scopedStats.byMonth, monthShortLabels]
  );

  const yearTrendOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false as const,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { position: 'top' as const, labels: { font: chartFont, color: '#333' } },
        title: { display: false },
      },
      scales: {
        x: { ticks: { font: chartFont, color: '#555' }, grid: { color: 'rgba(0,0,0,0.06)' } },
        y: {
          beginAtZero: true,
          ticks: { font: chartFont, color: '#555' },
          grid: { color: 'rgba(0,0,0,0.06)' },
        },
      },
    }),
    [selectedYear]
  );

  const topEntriesBarData = useMemo(
    () => ({
      labels: scopedStats.topByEntries.map((r) => r.name),
      datasets: [
        {
          label: 'Einsätze im Gerät',
          data: scopedStats.topByEntries.map((r) => r.count),
          backgroundColor: ASM_RED,
          borderColor: ASM_RED,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }),
    [scopedStats.topByEntries]
  );

  const topDurationBarData = useMemo(
    () => ({
      labels: scopedStats.topByDuration.map((r) => r.name),
      datasets: [
        {
          label: 'Minuten',
          data: scopedStats.topByDuration.map((r) => Number(r.minutes.toFixed(1))),
          backgroundColor: CHART_ORANGE,
          borderColor: CHART_ORANGE,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }),
    [scopedStats.topByDuration]
  );

  const horizontalBarOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false as const,
      layout: { padding: { right: 56, left: 4 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset?: { label?: string }; parsed?: { x?: number } }) => {
              const v = ctx.parsed?.x;
              if (ctx.dataset?.label === 'Minuten' && typeof v === 'number') {
                return `${v.toFixed(1)} min`;
              }
              return String(v ?? '');
            },
          },
        },
      },
      scales: {
        x: { beginAtZero: true, ticks: { font: chartFont, color: '#555' }, grid: { color: 'rgba(0,0,0,0.06)' } },
        y: { ticks: { font: chartFont, color: '#333', autoSkip: false } as const, grid: { display: false } },
      },
    }),
    []
  );

  const intensityTrendChartData = useMemo(
    () => ({
      labels: monthShortLabels,
      datasets: [
        {
          label: 'Mayday je Einsatz',
          data: scopedStats.byMonth.map((m) =>
            m.operationCount > 0 ? m.maydayCount / m.operationCount : 0
          ),
          borderColor: CHART_MAYDAY,
          backgroundColor: 'rgba(136, 14, 79, 0.12)',
          tension: 0.25,
          fill: true,
          yAxisID: 'y',
        },
        {
          label: 'Trupps je Einsatz',
          data: scopedStats.byMonth.map((m) =>
            m.operationCount > 0 ? m.teamStartCount / m.operationCount : 0
          ),
          borderColor: CHART_ORANGE,
          backgroundColor: 'rgba(230, 81, 0, 0.1)',
          tension: 0.25,
          fill: true,
          yAxisID: 'y1',
        },
      ],
    }),
    [scopedStats.byMonth, monthShortLabels]
  );

  const intensityTrendOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false as const,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { position: 'top' as const, labels: { font: chartFont, color: '#333' } },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset?: { label?: string }; parsed?: { y: number } }) => {
              const v = ctx.parsed?.y;
              if (typeof v !== 'number') return '';
              if (ctx.dataset?.label === 'Mayday je Einsatz') {
                return `Mayday je Einsatz: ${formatRatio(v, 3)}`;
              }
              return `Trupps je Einsatz: ${formatRatio(v, 2)}`;
            },
          },
        },
      },
      scales: {
        x: { ticks: { font: chartFont, color: '#555' }, grid: { color: 'rgba(0,0,0,0.06)' } },
        y: {
          type: 'linear' as const,
          position: 'left' as const,
          beginAtZero: true,
          title: { display: true, text: 'Mayday / Einsatz', color: '#555', font: chartFont },
          ticks: { font: chartFont, color: CHART_MAYDAY },
          grid: { color: 'rgba(0,0,0,0.06)' },
        },
        y1: {
          type: 'linear' as const,
          position: 'right' as const,
          beginAtZero: true,
          title: { display: true, text: 'Trupps / Einsatz', color: '#555', font: chartFont },
          ticks: { font: chartFont, color: CHART_ORANGE },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    [selectedYear]
  );

  const createPdf = async (scope: 'year' | 'month') => {
    const redHead = {
      fillColor: [201, 31, 12] as [number, number, number],
      textColor: [255, 255, 255] as [number, number, number],
      fontStyle: 'bold' as const,
    };

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const title =
      scope === 'year'
        ? `Jahresstatistik ${selectedYear}`
        : `Monatsstatistik ${monthNames[selectedMonth as number]} ${selectedYear}`;

    doc.setFontSize(18);
    doc.setTextColor(201, 31, 12);
    doc.text(title, 14, 16);
    doc.setTextColor(0, 0, 0);

    const summaryRows = [
      ['Atemschutzeinsätze', String(scopedStats.operationCount)],
      ['Trupp gestartet (Anzahl)', String(scopedStats.teamStartCount)],
      ['Mayday-Ereignisse', String(scopedStats.maydayCount)],
      ['Aktive Truppüberwacher', String(scopedStats.uniqueSupervisors)],
      ['Ø Einsatzdauer pro Trupp', formatMinutes(scopedStats.avgTeamDurationMinutes)],
      ['Gesamte Trupp-Zeit', formatMinutes(scopedStats.totalTeamDurationMinutes)],
      ['Ø Trupps je Truppüberwacher (Proxy)', formatRatio(scopedStats.avgTeamsPerSupervisor, 2)],
      ['Mayday je Einsatz', formatRatio(scopedStats.maydayPerOperation, 3)],
      ['Ø Truppüberwacher je Einsatz', formatRatio(scopedStats.avgSupervisorsPerOperation, 2)],
      ['Ø Trupps je Einsatz', formatRatio(scopedStats.avgTeamsPerOperation, 2)],
      ['Ø Min. im Gerät pro Atemschützer', formatMinutes(scopedStats.avgMinutesPerResponder)],
    ];

    (doc as any).autoTable({
      startY: 24,
      head: [['Kennzahl', 'Wert']],
      body: summaryRows,
      theme: 'grid',
      styles: { fontSize: 10 },
      headStyles: redHead,
      margin: { bottom: 24 },
    });

    const yAfterSummary = (doc as any).lastAutoTable.finalY + 6;

    const countByName = new Map(scopedStats.topByEntries.map((r) => [r.name, r.count]));
    const minutesByName = new Map(scopedStats.topByDuration.map((r) => [r.name, r.minutes]));
    const personRowsOrder: string[] = [];
    const seenName = new Set<string>();
    for (const r of scopedStats.topByEntries) {
      if (!seenName.has(r.name)) {
        personRowsOrder.push(r.name);
        seenName.add(r.name);
      }
    }
    for (const r of scopedStats.topByDuration) {
      if (!seenName.has(r.name)) {
        personRowsOrder.push(r.name);
        seenName.add(r.name);
      }
    }

    // Best-effort: Rang aus DB (rankShort, rankPrio) anhand Vor-/Nachname auflösen
    const normalizeNameToken = (s: string): string =>
      s
        .trim()
        .toLowerCase()
        .replace(/[.,]/g, '')
        .replace(/\s+/g, ' ');

    const rankInfoByFullNameKey = new Map<string, { rankShort: string; rankPrio: number }>();
    try {
      const users = await adminService.getUsers(orgID);
      users.forEach((u) => {
        const first = (u.firstname || '').trim();
        const last = (u.lastname || '').trim();
        const rankShort = (u.rankShort || '').trim();
        const rankPrioRaw = (u as any)?.rankPrio;
        const rankPrio = typeof rankPrioRaw === 'number' ? rankPrioRaw : Number(rankPrioRaw);
        if (!first || !last || !rankShort) return;
        if (!Number.isFinite(rankPrio)) return;
        const a = `${normalizeNameToken(first)} ${normalizeNameToken(last)}`;
        const b = `${normalizeNameToken(last)} ${normalizeNameToken(first)}`;
        rankInfoByFullNameKey.set(a, { rankShort, rankPrio });
        rankInfoByFullNameKey.set(b, { rankShort, rankPrio });
      });
    } catch {
      // ignore: PDF funktioniert auch ohne Rang-Auflösung
    }

    const getRankInfoForFullName = (fullName: string): { rankShort: string; rankPrio: number } | null => {
      const parts = fullName.trim().split(/\s+/).filter(Boolean);
      if (parts.length < 2) return null;
      // Try both orders (Vorname Nachname) and (Nachname Vorname)
      const lastToken = normalizeNameToken(parts[parts.length - 1]);
      const firstTokens = normalizeNameToken(parts.slice(0, -1).join(' '));
      const keyA = `${firstTokens} ${lastToken}`;
      const keyB = `${lastToken} ${firstTokens}`;
      return rankInfoByFullNameKey.get(keyA) || rankInfoByFullNameKey.get(keyB) || null;
    };

    const formatPdfPersonName = (fullName: string): string => {
      const parts = fullName.trim().split(/\s+/).filter(Boolean);
      if (parts.length < 2) return fullName;
      const lastName = parts[parts.length - 1];
      const firstNames = parts.slice(0, -1).join(' ');
      return `${lastName} ${firstNames}`;
    };

    const formatForSort = (fullName: string): string => normalizeNameToken(formatPdfPersonName(fullName));

    const sortedPersons = [...personRowsOrder].sort((a, b) => {
      const ra = getRankInfoForFullName(a)?.rankPrio ?? Number.POSITIVE_INFINITY;
      const rb = getRankInfoForFullName(b)?.rankPrio ?? Number.POSITIVE_INFINITY;
      if (ra !== rb) return ra - rb;
      return formatForSort(a).localeCompare(formatForSort(b), 'de');
    });

    const personTableBody = sortedPersons.map((name) => {
      const count = countByName.get(name);
      const minutes = minutesByName.get(name);
      const avgMinutes = typeof count === 'number' && typeof minutes === 'number' && count > 0 ? minutes / count : null;
      const rankInfo = getRankInfoForFullName(name);
      const displayName = rankInfo?.rankShort ? `${rankInfo.rankShort} ${formatPdfPersonName(name)}` : formatPdfPersonName(name);

      return [
        displayName,
        typeof count === 'number' ? String(count) : '–',
        typeof minutes === 'number' ? formatMinutes(minutes) : '–',
        avgMinutes === null ? '–' : formatMinutes(avgMinutes),
      ];
    });

    const pdfPageW = doc.internal.pageSize.getWidth();
    const leftMargin = 20;
    const rightMargin = 20;
    const availableW = pdfPageW - leftMargin - rightMargin;
    const col0 = Math.round(availableW * 0.55); // Person
    const remaining = availableW - col0;
    const col1 = Math.round(remaining * 0.25); // Wie oft
    const col2 = Math.round(remaining * 0.35); // Zeit
    const col3 = remaining - col1 - col2; // Ø Zeit

    (doc as any).autoTable({
      startY: yAfterSummary,
      head: [['Person', 'Wie oft im Gerät', 'Zeit im Gerät', 'Ø Zeit pro Einsatz']],
      body: personTableBody,
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: redHead,
      columnStyles: {
        0: { cellWidth: col0 },
        1: { cellWidth: col1, halign: 'right' },
        2: { cellWidth: col2, halign: 'right' },
        3: { cellWidth: col3, halign: 'right' },
      },
      margin: { left: leftMargin, right: rightMargin, bottom: 24 },
    });

    if (scope === 'year') {
      const yAfterPersons = (doc as any).lastAutoTable.finalY + 6;
      (doc as any).autoTable({
        startY: yAfterPersons,
        head: [['Monat', 'Atemschutzeinsätze', 'Trupp gestartet', 'Mayday']],
        body: scopedStats.byMonth.map((m) => [
          m.month,
          String(m.operationCount),
          String(m.teamStartCount),
          String(m.maydayCount),
        ]),
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: redHead,
        margin: { bottom: 24 },
      });
    }

    // Footer: Logo + Hinweise + Seitenzahlen (alle Seiten)
    const loadLogoDataUrl = async (url: string): Promise<string | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const logoDataUrl = await loadLogoDataUrl('/images/logo_landscape.png');

    const tintToAsmRedPngDataUrl = async (dataUrl: string): Promise<string | null> => {
      try {
        const img = new Image();
        img.src = dataUrl;

        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image load failed'));
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255; // 0..1
          // Tint towards ASMonitor red while keeping luminance details.
          pixels[i] = Math.round(201 * lum);
          pixels[i + 1] = Math.round(31 * lum);
          pixels[i + 2] = Math.round(12 * lum);
        }
        ctx.putImageData(imageData, 0, 0);

        return canvas.toDataURL('image/png');
      } catch {
        return null;
      }
    };

    const getImageAspectRatio = async (dataUrl: string): Promise<number | null> => {
      try {
        const img = new Image();
        img.src = dataUrl;
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image load failed'));
        });
        if (!img.width || !img.height) return null;
        return img.width / img.height;
      } catch {
        return null;
      }
    };

    const redLogoDataUrl = logoDataUrl ? await tintToAsmRedPngDataUrl(logoDataUrl) : null;
    const footerLogoDataUrl = redLogoDataUrl || logoDataUrl;
    const logoAspect = footerLogoDataUrl ? await getImageAspectRatio(footerLogoDataUrl) : null;
    const pageCount = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Footer line
      doc.setDrawColor(224, 224, 224);
      doc.setLineWidth(0.2);
      doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);

      // Logo + Text (unten links, Text unter Logo)
      const logoX = 22;
      const maxLogoW = 26; // mm
      const maxLogoH = 9; // mm
      const safeAspect = logoAspect && logoAspect > 0 ? logoAspect : 3;
      const logoW = Math.min(maxLogoW, maxLogoH * safeAspect);
      const logoH = logoW / safeAspect;
      const logoY = pageHeight - 19 + (maxLogoH - logoH) / 2; // vertically centered in footer slot

      if (footerLogoDataUrl) {
        try {
          doc.addImage(footerLogoDataUrl, 'PNG', logoX, logoY, logoW, logoH);
        } catch {
          // ignore logo errors
        }
      }

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Generiert durch AS Monitor', logoX + logoW / 2, pageHeight - 5.5, { align: 'center' });

      // Page number centered (bottom)
      doc.text(`Seite ${i} von ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    }

    const scopeLabel =
      scope === 'year'
        ? `jahr_${selectedYear}`
        : `monat_${selectedYear}_${String((selectedMonth as number) + 1).padStart(2, '0')}`;
    doc.save(`asm_statistik_${scopeLabel}.pdf`);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'end',
          flexWrap: 'wrap',
          marginBottom: 20,
          padding: 14,
          background: '#f8f9fb',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
        }}
      >
        <div style={{ minWidth: 160 }}>
          <label style={{ display: 'block', marginBottom: 6, color: '#333', fontWeight: 600 }}>Jahr</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cfd4dc', background: '#fff' }}
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 220 }}>
          <label style={{ display: 'block', marginBottom: 6, color: '#333', fontWeight: 600 }}>Monat</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #cfd4dc', background: '#fff' }}
          >
            <option value="all">Ganzes Jahr</option>
            {monthNames.map((m, i) => (
              <option key={m} value={i}>
                {m}
              </option>
            ))}
          </select>
        </div>
        {selectedMonth === 'all' ? (
          <button
            onClick={() => void createPdf('year')}
            style={{ padding: '10px 14px', border: 'none', borderRadius: 6, background: '#c91f0c', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
          >
            PDF Jahr
          </button>
        ) : (
          <button
            onClick={() => void createPdf('month')}
            style={{ padding: '10px 14px', border: 'none', borderRadius: 6, background: '#c91f0c', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
          >
            PDF Monat
          </button>
        )}
        {backgroundLoading && (
          <div style={{ alignSelf: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: '3px solid rgba(201, 31, 12, 0.18)',
                borderTop: '3px solid rgba(201, 31, 12, 0.9)',
                animation: 'asm-spin 0.8s linear infinite',
              }}
            />
            <div style={{ fontSize: 12, color: '#666', textAlign: 'center', lineHeight: 1.2 }}>
              Weitere Einsätze des Jahres werden geladen…
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 220,
            gap: 14,
          }}
        >
          <style>{`
            @keyframes asm-spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: '5px solid #f4c7c1',
              borderTop: '5px solid #c91f0c',
              animation: 'asm-spin 0.8s linear infinite',
            }}
          />
          <div style={{ color: '#c91f0c', fontWeight: 700, fontSize: 16 }}>Statistiken werden geladen...</div>
        </div>
      )}
      {error && <div style={{ color: '#c91f0c' }}>{error}</div>}

      {!loading && !error && (
        <div>
          <div style={{ ...statsPanelShell, marginBottom: 18 }}>
            <h3 style={statsPanelTitle}>Kurzüberblick</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 160px), 1fr))',
                gap: 12,
              }}
            >
              {(
                [
                  {
                    key: 'ops',
                    label: 'Atemschutzeinsätze',
                    value: String(scopedStats.operationCount),
                    current: scopedStats.operationCount,
                    previous: prevMonthStats ? prevMonthStats.operationCount : null,
                    accent: kpiAccent.ops,
                  },
                  {
                    key: 'teamStart',
                    label: 'Trupp gestartet',
                    value: String(scopedStats.teamStartCount),
                    current: scopedStats.teamStartCount,
                    previous: prevMonthStats ? prevMonthStats.teamStartCount : null,
                    accent: kpiAccent.team,
                  },
                  {
                    key: 'mayday',
                    label: 'Mayday',
                    value: String(scopedStats.maydayCount),
                    current: scopedStats.maydayCount,
                    previous: prevMonthStats ? prevMonthStats.maydayCount : null,
                    accent: kpiAccent.mayday,
                  },
                  {
                    key: 'sup',
                    label: 'Truppüberwacher',
                    value: String(scopedStats.uniqueSupervisors),
                    current: scopedStats.uniqueSupervisors,
                    previous: prevMonthStats ? prevMonthStats.uniqueSupervisors : null,
                    accent: kpiAccent.supervisors,
                  },
                  {
                    key: 'avgDur',
                    label: 'Ø Dauer pro Trupp',
                    value: formatMinutes(scopedStats.avgTeamDurationMinutes),
                    current: scopedStats.avgTeamDurationMinutes,
                    previous: prevMonthStats ? prevMonthStats.avgTeamDurationMinutes : null,
                    accent: kpiAccent.avgDur,
                  },
                  {
                    key: 'totalDur',
                    label: 'Gesamte Trupp-Zeit',
                    value: formatMinutes(scopedStats.totalTeamDurationMinutes),
                    current: scopedStats.totalTeamDurationMinutes,
                    previous: prevMonthStats ? prevMonthStats.totalTeamDurationMinutes : null,
                    accent: kpiAccent.totalDur,
                  },
                ] as const
              ).map((k) => {
                const pct = k.previous === null ? null : relChangePercent(k.current, k.previous);
                const trendColor =
                  pct === null
                    ? '#9aa3ad'
                    : Math.abs(pct) < 0.05
                      ? '#9aa3ad'
                      : pct > 0
                        ? '#2e7d32'
                        : '#c62828';
                const direction: 'up' | 'down' | null =
                  pct === null ? null : Math.abs(pct) < 0.05 ? null : pct > 0 ? 'up' : 'down';
                const trendText =
                  k.previous === null
                    ? '—'
                    : pct === null
                      ? k.previous === 0 && k.current !== 0
                        ? 'neu'
                        : k.previous === 0 && k.current === 0
                          ? '±0 %'
                          : '—'
                      : Math.abs(pct) < 0.05
                        ? '±0 %'
                        : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)} %`;

                const TrendArrowMini: React.FC<{ direction: 'up' | 'down'; color: string }> = ({ direction, color }) => (
                  <svg width="12" height="10" viewBox="0 0 12 10" style={{ display: 'block' }}>
                    {direction === 'up' ? (
                      <>
                        <path d="M6 9 L6 2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M6 2 L3.4 4.4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M6 2 L8.6 4.4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
                      </>
                    ) : (
                      <>
                        <path d="M6 1 L6 8" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M6 8 L3.4 5.6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M6 8 L8.6 5.6" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
                      </>
                    )}
                  </svg>
                );

                return (
                  <div
                    key={k.key}
                    style={{
                      borderRadius: 8,
                      padding: '12px 14px',
                      background: '#f9fafc',
                      border: '1px solid #e8eaed',
                      borderLeft: `4px solid ${k.accent}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: '#5c6370',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        lineHeight: 1.3,
                      }}
                    >
                      {k.label}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#121212', marginTop: 8, lineHeight: 1.15 }}>{k.value}</div>
                    <div style={{ marginTop: 8, fontSize: 12, color: trendColor, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {direction ? <TrendArrowMini direction={direction} color={trendColor} /> : null}
                        <span style={{ fontWeight: 700 }}>{trendText}</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                selectedMonth !== 'all' && prevMonthStats
                  ? 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))'
                  : '1fr',
              gap: 24,
              alignItems: 'stretch',
              marginBottom: 20,
            }}
          >
            <div style={{ ...statsPanelShell, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <h3 style={statsPanelTitle}>Einordnung & Belastung</h3>
              <p style={statsPanelIntro}>
                Verhältniszahlen pro Einsatz und pro Beteiligten. „Trupps je Truppüberwacher“ ist ein Proxy aus Starts ÷
                eindeutige Namen in den Logs.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    selectedMonth !== 'all' && prevMonthStats
                      ? 'repeat(2, minmax(0, 1fr))'
                      : 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 12,
                  flex: 1,
                }}
              >
                {(
                  [
                    {
                      title: 'Ø Trupps je Truppüberwacher',
                      value: formatRatio(scopedStats.avgTeamsPerSupervisor, 2),
                      sub:
                        scopedStats.uniqueSupervisors > 0
                          ? `${scopedStats.teamStartCount} Starts · ${scopedStats.uniqueSupervisors} Namen in Logs`
                          : 'Keine Überwacher in Log-Daten',
                      accent: kpiAccent.supervisors,
                    },
                    {
                      title: 'Mayday je Einsatz',
                      value: formatRatio(scopedStats.maydayPerOperation, 3),
                      sub:
                        scopedStats.operationCount > 0
                          ? `${scopedStats.maydayCount} Mayday · ${scopedStats.operationCount} Einsätze`
                          : 'Keine Einsätze im Zeitraum',
                      accent: kpiAccent.mayday,
                    },
                    {
                      title: 'Ø Truppüberwacher je Einsatz',
                      value: formatRatio(scopedStats.avgSupervisorsPerOperation, 2),
                      sub: 'Eindeutige Namen (performedBy) je Einsatz gemittelt',
                      accent: kpiAccent.insight,
                    },
                    {
                      title: 'Ø Trupps je Einsatz',
                      value: formatRatio(scopedStats.avgTeamsPerOperation, 2),
                      sub: 'Gestartete Trupps geteilt durch Einsätze',
                      accent: kpiAccent.team,
                    },
                    {
                      title: 'Ø Min. im Gerät pro Atemschützer',
                      value: formatMinutes(scopedStats.avgMinutesPerResponder),
                      sub:
                        scopedStats.respondersWithTimeCount > 0
                          ? `${scopedStats.respondersWithTimeCount} Personen mit messbarer Zeit`
                          : 'Keine Zeiten erfasst',
                      accent: kpiAccent.avgDur,
                    },
                    {
                      title: 'Summe Atemschutz-Minuten (Personen)',
                      value: formatMinutes(scopedStats.totalMemberMinutesSum),
                      sub: 'Alle Einsätze von Atemschützern addiert (Mehrfacheinsätze zählen mehrfach)',
                      accent: kpiAccent.totalDur,
                    },
                  ] as const
                ).map((card) => (
                  <div
                    key={card.title}
                    style={{
                      borderRadius: 8,
                      padding: '12px 14px',
                      background: '#f9fafc',
                      border: '1px solid #e8eaed',
                      borderTop: `3px solid ${card.accent}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: '#5c6370',
                        fontWeight: 600,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        lineHeight: 1.35,
                      }}
                    >
                      {card.title}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#121212', marginTop: 6 }}>{card.value}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 5, lineHeight: 1.4 }}>{card.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {selectedMonth !== 'all' && prevMonthStats && (
              <div
                style={{
                  ...statsPanelShell,
                  position: 'sticky',
                  top: 12,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h3 style={statsPanelTitle}>Trend zum Vormonat</h3>
                <p style={statsPanelPeriod}>
                  Vergleich mit {monthNames[selectedMonth === 0 ? 11 : selectedMonth - 1]} {selectedYear}
                </p>
                <p style={statsPanelIntro}>
                Grün = steigt · Rot = fällt (je Einsatz/je Beteiligten im Vergleich zum Vormonat).
                </p>
                <InsightMomRow
                  label="Ø Zeit im Gerät pro Trupp"
                  current={scopedStats.avgTeamDurationMinutes}
                  previous={prevMonthStats.avgTeamDurationMinutes}
                  format={(n) => formatMinutes(n)}
                />
                <InsightMomRow
                  label="Ø Zeit im Gerät pro Atemschützer"
                  current={scopedStats.avgMinutesPerResponder}
                  previous={prevMonthStats.avgMinutesPerResponder}
                  format={(n) => formatMinutes(n)}
                />
                <InsightMomRow
                  label="Mayday je Einsatz"
                  current={scopedStats.maydayPerOperation}
                  previous={prevMonthStats.maydayPerOperation}
                  format={(n) => formatRatio(n, 3)}
                />
                <InsightMomRow
                  label="Trupps je Einsatz"
                  current={scopedStats.avgTeamsPerOperation}
                  previous={prevMonthStats.avgTeamsPerOperation}
                  format={(n) => formatRatio(n, 2)}
                />
                <InsightMomRow
                  label="Trupps je Truppüberwacher"
                  current={scopedStats.avgTeamsPerSupervisor}
                  previous={prevMonthStats.avgTeamsPerSupervisor}
                  format={(n) => formatRatio(n, 2)}
                />
                <InsightMomRow
                  label="Atemschutzeinsätze (Anzahl)"
                  current={scopedStats.operationCount}
                  previous={prevMonthStats.operationCount}
                  format={(n) => String(Math.round(n))}
                />
                <div style={statsPanelFooter}>
                  {selectedMonth === 0
                    ? `Vormonat = Dezember ${selectedYear - 1} (Vorjahr).`
                    : `Vormonat = Kalenderjahr ${selectedYear}.`}
                </div>
              </div>
            )}
          </div>

          {selectedMonth === 'all' && (
            <>
              <div style={{ ...statsPanelShell, marginBottom: 16 }}>
                <h3 style={statsPanelTitle}>Jahresverlauf</h3>
                <p style={{ ...statsPanelIntro, marginBottom: 10 }}>
                  Atemschutzeinsätze, gestartete Trupps und Mayday-Ereignisse pro Monat.
                </p>
                <div style={{ height: 280, position: 'relative' }}>
                  <Line data={yearTrendChartData} options={yearTrendOptions} />
                </div>
              </div>
              <div style={{ ...statsPanelShell, marginBottom: 20 }}>
                <h3 style={statsPanelTitle}>Einsatzintensität (je Einsatz)</h3>
                <p style={{ ...statsPanelIntro, marginBottom: 10 }}>
                  Mayday und Trupps pro Einsatz – zwei Skalen (links / rechts).
                </p>
                <div style={{ height: 260, position: 'relative' }}>
                  <Line data={intensityTrendChartData} options={intensityTrendOptions} />
                </div>
              </div>
            </>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 14,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                ...statsPanelShell,
                minHeight: Math.max(220, scopedStats.topByEntries.length * 32 + 100),
              }}
            >
              <h3 style={statsPanelTitle}>Top 10 – wie oft im Gerät</h3>
              {scopedStats.topByEntries.length === 0 ? (
                <div style={{ color: '#666', fontSize: 14 }}>Keine Daten</div>
              ) : (
                <div style={{ height: Math.max(160, scopedStats.topByEntries.length * 32 + 40), position: 'relative' }}>
                  <Bar
                    data={topEntriesBarData}
                    options={horizontalBarOptions}
                    plugins={[horizontalBarInlineValuesPlugin('count')]}
                  />
                </div>
              )}
            </div>
            <div
              style={{
                ...statsPanelShell,
                minHeight: Math.max(220, scopedStats.topByDuration.length * 32 + 100),
              }}
            >
              <h3 style={statsPanelTitle}>Top 10 – Zeit im Gerät</h3>
              {scopedStats.topByDuration.length === 0 ? (
                <div style={{ color: '#666', fontSize: 14 }}>Keine Daten</div>
              ) : (
                <div style={{ height: Math.max(160, scopedStats.topByDuration.length * 32 + 40), position: 'relative' }}>
                  <Bar
                    data={topDurationBarData}
                    options={horizontalBarOptions}
                    plugins={[horizontalBarInlineValuesPlugin('minutes')]}
                  />
                </div>
              )}
            </div>
          </div>

          {selectedMonth === 'all' && (
            <div style={{ ...statsPanelShell, marginTop: 16 }}>
              <h3 style={statsPanelTitle}>Monatsübersicht</h3>
              <p style={statsPanelPeriod}>Ganzes Jahr {selectedYear}</p>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Monat</th>
                    <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Atemschutzeinsätze</th>
                    <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Trupp gestartet</th>
                    <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Mayday</th>
                  </tr>
                </thead>
                <tbody>
                  {scopedStats.byMonth.map((m) => (
                    <tr key={m.month}>
                      <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{m.month}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{m.operationCount}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{m.teamStartCount}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{m.maydayCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YearlyStatistics;
